<script setup>
import './jov-tickets-theme.css';
import {
  computed,
  onMounted,
  ref,
  watch,
} from 'vue';

import {
  useRoute,
  useRouter,
} from 'vue-router';

import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import TicketsAPI from 'dashboard/api/tickets';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

import TicketCreateModal from './TicketCreateModal.vue';
import TicketTimeline from './TicketTimeline.vue';

const route = useRoute();
const router = useRouter();

const tickets = ref([]);
const statuses = ref([]);
const categories = ref([]);

const activeTicketDetail = ref(null);

const isLoadingTickets = ref(false);
const isLoadingTicket = ref(false);
const isUpdatingTicket = ref(false);
const isCreateModalOpen = ref(false);

const search = ref('');
const selectedStatusFilter = ref('');

const meta = ref({
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
  perPage: 50,
});

const currentRole =
  useMapGetter('getCurrentRole');

const isAdministrator = computed(
  () => currentRole.value === 'administrator'
);

const activeTicketId = computed(() => {
  const value =
    Number(route.params.ticketId);

  return Number.isFinite(value) ?
    value :
    0;
});

const activeTicket = computed(() => {
  if (
    activeTicketDetail.value &&
    Number(activeTicketDetail.value.id) ===
      Number(activeTicketId.value)
  ) {
    return activeTicketDetail.value;
  }

  return (
    tickets.value.find(
      ticket =>
        Number(ticket.id) ===
        Number(activeTicketId.value)
    ) ||
    null
  );
});

const priorityOptions = [
  {
    value: '',
    label: 'Sem prioridade',
  },
  {
    value: 'low',
    label: 'Baixa',
  },
  {
    value: 'medium',
    label: 'Média',
  },
  {
    value: 'high',
    label: 'Alta',
  },
  {
    value: 'urgent',
    label: 'Urgente',
  },
];

const priorityLabel = value => {
  return (
    priorityOptions.find(
      option => option.value === value
    )?.label ||
    'Sem prioridade'
  );
};

const formatDate = value => {
  if (!value) return '—';

  const date =
    new Date(Number(value) * 1000);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleString('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
};

const statusStyle = ticket => {
  const color =
    ticket?.status?.color;

  if (!color) return {};

  return {
    borderColor: color,
    color,
  };
};

// JOV_V4_VIEW

const ticketInitials = ticket => {
  const name =
    ticket?.contact?.name?.trim() ||
    'Contato';

  return name
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');
};

const ticketStatusColor = ticket => {
  return ticket?.status?.color || '#6c4dff';
};

const priorityTone = value => {
  return {
    urgent: 'is-urgent',
    high: 'is-high',
    medium: 'is-medium',
    low: 'is-low',
  }[value] || 'is-none';
};

const compactDate = value => {
  if (!value) return '—';

  const date =
    new Date(Number(value) * 1000);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const originLabel = ticket => {
  return (
    ticket?.origin?.label ||
    ticket?.origin?.display ||
    ticket?.source ||
    'Origem não informada'
  );
};

const applyStatusFilter = value => {
  selectedStatusFilter.value =
    value ? String(value) : '';

  applyFilters();
};


const replaceTicket = updatedTicket => {
  if (!updatedTicket?.id) return;

  const index =
    tickets.value.findIndex(
      ticket =>
        Number(ticket.id) ===
        Number(updatedTicket.id)
    );

  const listVersion = {
    ...updatedTicket,
  };

  delete listVersion.messages;

  if (index === -1) {
    tickets.value.unshift(listVersion);
  } else {
    tickets.value.splice(
      index,
      1,
      {
        ...tickets.value[index],
        ...listVersion,
      }
    );
  }

  if (
    Number(updatedTicket.id) ===
    Number(activeTicketId.value)
  ) {
    activeTicketDetail.value =
      updatedTicket;
  }
};

const fetchStatuses = async () => {
  try {
    const response =
      await TicketsAPI.statuses();

    statuses.value =
      response.data?.payload || [];
  } catch {
    statuses.value = [];
  }
};

const fetchCategories = async () => {
  try {
    const response =
      await TicketsAPI.categories();

    categories.value =
      response.data?.payload || [];
  } catch {
    categories.value = [];
  }
};

const fetchTickets = async ({
  preserveSelection = true,
} = {}) => {
  isLoadingTickets.value = true;

  try {
    const params = {
      page: 1,
      per_page: 50,
    };

    if (search.value.trim()) {
      params.q = search.value.trim();
    }

    if (selectedStatusFilter.value) {
      params.status_id =
        selectedStatusFilter.value;
    }

    const response =
      await TicketsAPI.index(params);

    const data =
      response.data?.data || {};

    tickets.value =
      data.payload || [];

    meta.value = {
      currentPage:
        data.meta?.current_page || 1,
      totalPages:
        data.meta?.total_pages || 1,
      totalCount:
        data.meta?.total_count || 0,
      perPage:
        data.meta?.per_page || 50,
    };

    const selectedStillExists =
      tickets.value.some(
        ticket =>
          Number(ticket.id) ===
          Number(activeTicketId.value)
      );

    if (
      tickets.value.length &&
      (
        !preserveSelection ||
        !activeTicketId.value ||
        !selectedStillExists
      )
    ) {
      await router.replace({
        name: 'jov_tickets_show',
        params: {
          accountId:
            route.params.accountId,
          ticketId:
            tickets.value[0].id,
        },
      });
    }

    if (
      !tickets.value.length &&
      activeTicketId.value
    ) {
      activeTicketDetail.value = null;

      await router.replace({
        name: 'jov_tickets_index',
        params: {
          accountId:
            route.params.accountId,
        },
      });
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'Não foi possível carregar os tickets.'
    );
  } finally {
    isLoadingTickets.value = false;
  }
};

const fetchSingleTicket = async ticketId => {
  if (!ticketId) {
    activeTicketDetail.value = null;
    return;
  }

  isLoadingTicket.value = true;

  try {
    const response =
      await TicketsAPI.show(ticketId);

    replaceTicket(response.data);
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'Não foi possível carregar o ticket.'
    );
  } finally {
    isLoadingTicket.value = false;
  }
};

const selectTicket = ticket => {
  if (
    Number(ticket.id) ===
    Number(activeTicketId.value)
  ) {
    return;
  }

  router.push({
    name: 'jov_tickets_show',
    params: {
      accountId:
        route.params.accountId,
      ticketId:
        ticket.id,
    },
  });
};

const changeStatus = async event => {
  if (
    !activeTicket.value ||
    !event.target.value
  ) {
    return;
  }

  isUpdatingTicket.value = true;

  try {
    const response =
      await TicketsAPI.changeStatus(
        activeTicket.value.id,
        Number(event.target.value)
      );

    replaceTicket(response.data);

    await fetchSingleTicket(
      activeTicket.value.id
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'Não foi possível alterar o status.'
    );
  } finally {
    isUpdatingTicket.value = false;
  }
};

const changeCategory = async event => {
  if (!activeTicket.value) return;

  isUpdatingTicket.value = true;

  try {
    const value =
      event.target.value;

    const response =
      await TicketsAPI.update(
        activeTicket.value.id,
        {
          ticket: {
            category_id:
              value ?
                Number(value) :
                null,
          },
        }
      );

    replaceTicket(response.data);

    await fetchSingleTicket(
      activeTicket.value.id
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'Não foi possível alterar a categoria.'
    );
  } finally {
    isUpdatingTicket.value = false;
  }
};

const changePriority = async event => {
  if (!activeTicket.value) return;

  isUpdatingTicket.value = true;

  try {
    const response =
      await TicketsAPI.update(
        activeTicket.value.id,
        {
          ticket: {
            priority:
              event.target.value || null,
          },
        }
      );

    replaceTicket(response.data);

    await fetchSingleTicket(
      activeTicket.value.id
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'Não foi possível alterar a prioridade.'
    );
  } finally {
    isUpdatingTicket.value = false;
  }
};

const onTicketCreated = async ticket => {
  isCreateModalOpen.value = false;

  replaceTicket(ticket);

  await router.push({
    name: 'jov_tickets_show',
    params: {
      accountId:
        route.params.accountId,
      ticketId:
        ticket.id,
    },
  });

  await fetchTickets();
  await fetchSingleTicket(ticket.id);
};

const openTicketSettings = () => {
  router.push({
    name: 'settings_jov_tickets',
    params: {
      accountId:
        route.params.accountId,
    },
  });
};

const applyFilters = () => {
  fetchTickets({
    preserveSelection: false,
  });
};

watch(
  activeTicketId,
  async ticketId => {
    activeTicketDetail.value = null;

    if (ticketId) {
      await fetchSingleTicket(ticketId);
    }
  },
  {
    immediate: true,
  }
);

onMounted(async () => {
  await Promise.all([
    fetchStatuses(),
    fetchCategories(),
    fetchTickets(),
  ]);

  if (activeTicketId.value) {
    await fetchSingleTicket(
      activeTicketId.value
    );
  }
});
</script>

<template>
  <section class="jov-tickets-workspace">
    <aside class="jov-ticket-list-panel">
      <header class="jov-ticket-list-header">
        <div class="jov-ticket-list-heading">
          <div>
            <h1>Tickets</h1>

            <p>
              {{ meta.totalCount }}
              ticket{{ meta.totalCount === 1 ? '' : 's' }} no total
            </p>
          </div>

          <span class="jov-list-heading-icon">
            <span class="i-lucide-ticket-check" />
          </span>
        </div>

        <div class="jov-ticket-primary-actions">
          <button
            type="button"
            class="jov-primary-button"
            @click="isCreateModalOpen = true"
          >
            <span class="i-lucide-plus" />
            Novo ticket
          </button>

          <button
            v-if="isAdministrator"
            type="button"
            class="jov-icon-button"
            title="Configurações"
            @click="openTicketSettings"
          >
            <span class="i-lucide-settings-2" />
          </button>
        </div>

        <div class="jov-ticket-search">
          <span class="i-lucide-search" />

          <input
            v-model="search"
            type="search"
            placeholder="Buscar tickets..."
            @keyup.enter="applyFilters"
          />

          <button
            type="button"
            title="Atualizar"
            @click="applyFilters"
          >
            <span
              class="i-lucide-refresh-cw"
              :class="{
                'animate-spin': isLoadingTickets,
              }"
            />
          </button>
        </div>

        <div class="jov-ticket-filter-tabs">
          <button
            type="button"
            :class="{
              'is-active': !selectedStatusFilter,
            }"
            @click="applyStatusFilter('')"
          >
            Todos
          </button>

          <button
            v-for="status in statuses"
            :key="status.id"
            type="button"
            :class="{
              'is-active':
                Number(selectedStatusFilter) ===
                Number(status.id),
            }"
            @click="applyStatusFilter(status.id)"
          >
            <span
              class="jov-status-dot"
              :style="{
                backgroundColor:
                  status.color || '#8b8498',
              }"
            />

            {{ status.name }}
          </button>
        </div>
      </header>

      <div
        v-if="isLoadingTickets && !tickets.length"
        class="jov-empty-state"
      >
        <Spinner class="text-n-brand" />
      </div>

      <div
        v-else-if="!tickets.length"
        class="jov-empty-state"
      >
        <span class="i-lucide-ticket-x" />

        <strong>
          Nenhum ticket encontrado
        </strong>

        <p>
          Ajuste os filtros ou crie um novo ticket.
        </p>
      </div>

      <div
        v-else
        class="jov-ticket-list-scroll"
      >
        <button
          v-for="ticket in tickets"
          :key="ticket.id"
          type="button"
          class="jov-ticket-list-item"
          :class="{
            'is-active':
              Number(ticket.id) ===
              Number(activeTicketId),
          }"
          :style="{
            '--ticket-color':
              ticketStatusColor(ticket),
          }"
          @click="selectTicket(ticket)"
        >
          <div class="jov-ticket-item-top">
            <span class="jov-ticket-number">
              {{ ticket.number }}
            </span>

            <span
              class="jov-ticket-status-pill"
              :style="statusStyle(ticket)"
            >
              <span
                class="jov-status-dot"
                :style="{
                  backgroundColor:
                    ticketStatusColor(ticket),
                }"
              />

              {{
                ticket.status?.name ||
                'Sem status'
              }}
            </span>
          </div>

          <strong class="jov-ticket-item-subject">
            {{ ticket.subject }}
          </strong>

          <div class="jov-ticket-item-summary">
            <div class="jov-ticket-contact">
              <span class="jov-mini-avatar">
                {{ ticketInitials(ticket) }}
              </span>

              <span>
                {{
                  ticket.contact?.name ||
                  'Contato sem nome'
                }}
              </span>
            </div>

            <span
              class="jov-ticket-priority"
              :class="priorityTone(ticket.priority)"
            >
              {{ priorityLabel(ticket.priority) }}
            </span>
          </div>

          <div class="jov-ticket-item-footer">
            <span class="jov-ticket-origin">
              {{ originLabel(ticket) }}
            </span>

            <time>
              {{ compactDate(ticket.updated_at) }}
            </time>
          </div>
        </button>
      </div>
    </aside>

    <main class="jov-ticket-main-panel">
      <div
        v-if="!activeTicket"
        class="jov-empty-state"
      >
        <span class="i-lucide-ticket-check" />

        <strong>
          Selecione um ticket
        </strong>

        <p>
          O atendimento será exibido aqui.
        </p>
      </div>

      <TicketTimeline
        v-else
        :key="activeTicket.id"
        :ticket="activeTicket"
        :loading="isLoadingTicket"
        @refresh="
          fetchSingleTicket(
            activeTicket.id
          )
        "
      />
    </main>

    <aside
      v-if="activeTicket"
      class="jov-ticket-info-panel"
    >
      <header class="jov-info-header">
        <div>
          <span class="jov-info-eyebrow">
            Informações do Ticket
          </span>

          <h2>
            {{ activeTicket.subject }}
          </h2>
        </div>

        <span
          class="jov-ticket-status-pill"
          :style="statusStyle(activeTicket)"
        >
          <span
            class="jov-status-dot"
            :style="{
              backgroundColor:
                ticketStatusColor(activeTicket),
            }"
          />

          {{ activeTicket.status?.name }}
        </span>
      </header>

      <div class="jov-info-scroll">
        <section class="jov-info-card">
          <div class="jov-info-card-title">
            <h3>Classificação</h3>

            <span class="i-lucide-sliders-horizontal" />
          </div>

          <label class="jov-field">
            <span>Status</span>

            <select
              :value="activeTicket.status?.id"
              :disabled="isUpdatingTicket"
              @change="changeStatus"
            >
              <option
                v-for="status in statuses"
                :key="status.id"
                :value="status.id"
              >
                {{ status.name }}
              </option>
            </select>
          </label>

          <label class="jov-field">
            <span>Prioridade</span>

            <select
              :value="activeTicket.priority || ''"
              :disabled="isUpdatingTicket"
              @change="changePriority"
            >
              <option
                v-for="option in priorityOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>

          <label class="jov-field">
            <span>Categoria</span>

            <select
              :value="
                activeTicket.category?.id || ''
              "
              :disabled="isUpdatingTicket"
              @change="changeCategory"
            >
              <option value="">
                Sem categoria
              </option>

              <option
                v-for="category in categories"
                :key="category.id"
                :value="category.id"
              >
                {{ category.name }}
              </option>
            </select>
          </label>
        </section>

        <section class="jov-info-card">
          <div class="jov-info-card-title">
            <h3>Contato</h3>

            <span class="i-lucide-user-round" />
          </div>

          <div class="jov-contact-profile">
            <div class="jov-contact-avatar">
              {{ ticketInitials(activeTicket) }}
            </div>

            <div class="jov-contact-profile-data">
              <strong>
                {{
                  activeTicket.contact?.name ||
                  'Contato sem nome'
                }}
              </strong>

              <a
                v-if="activeTicket.contact?.email"
                :href="
                  `mailto:${activeTicket.contact.email}`
                "
              >
                <span class="i-lucide-mail" />
                {{ activeTicket.contact.email }}
              </a>

              <a
                v-if="
                  activeTicket.contact?.phone_number
                "
                :href="
                  `tel:${activeTicket.contact.phone_number}`
                "
              >
                <span class="i-lucide-phone" />

                {{
                  activeTicket.contact.phone_number
                }}
              </a>
            </div>
          </div>
        </section>

        <section class="jov-info-card">
          <div class="jov-info-card-title">
            <h3>Atribuição</h3>

            <span class="i-lucide-users-round" />
          </div>

          <div class="jov-info-row">
            <span>Responsável</span>

            <strong>
              {{
                activeTicket.assignee?.name ||
                'Não atribuído'
              }}
            </strong>
          </div>

          <div class="jov-info-row">
            <span>Equipe</span>

            <strong>
              {{
                activeTicket.team?.name ||
                'Sem equipe'
              }}
            </strong>
          </div>
        </section>

        <section class="jov-info-card">
          <div class="jov-info-card-title">
            <h3>Origem</h3>

            <span class="i-lucide-waypoints" />
          </div>

          <div class="jov-origin-card">
            <span class="i-lucide-plug-zap" />

            <div>
              <strong>
                {{ originLabel(activeTicket) }}
              </strong>

              <small
                v-if="
                  activeTicket.origin?.channel
                "
              >
                {{ activeTicket.origin.channel }}
              </small>
            </div>
          </div>
        </section>

        <section
          v-if="activeTicket.labels?.length"
          class="jov-info-card"
        >
          <div class="jov-info-card-title">
            <h3>Etiquetas</h3>

            <span class="i-lucide-tags" />
          </div>

          <div class="jov-label-list">
            <span
              v-for="label in activeTicket.labels"
              :key="
                label.id ||
                label.title ||
                label.name ||
                label
              "
            >
              {{
                label.title ||
                label.name ||
                label
              }}
            </span>
          </div>
        </section>

        <section class="jov-info-card">
          <div class="jov-info-card-title">
            <h3>Datas</h3>

            <span class="i-lucide-calendar-clock" />
          </div>

          <div class="jov-info-row">
            <span>Criado</span>

            <strong>
              {{
                formatDate(
                  activeTicket.created_at
                )
              }}
            </strong>
          </div>

          <div
            v-if="activeTicket.resolved_at"
            class="jov-info-row"
          >
            <span>Resolvido</span>

            <strong>
              {{
                formatDate(
                  activeTicket.resolved_at
                )
              }}
            </strong>
          </div>

          <div
            v-if="activeTicket.closed_at"
            class="jov-info-row"
          >
            <span>Fechado</span>

            <strong>
              {{
                formatDate(
                  activeTicket.closed_at
                )
              }}
            </strong>
          </div>
        </section>
      </div>
    </aside>

    <TicketCreateModal
      v-if="isCreateModalOpen"
      :statuses="statuses"
      :categories="categories"
      @close="isCreateModalOpen = false"
      @created="onTicketCreated"
    />
  </section>
</template>
